<template>
  <div class="vac-app">
    <!-- 월 이동 -->
    <div class="vac-nav">
      <div class="m">{{ navTitle }}</div>
      <div class="ctrl">
        <button @click="goToday">오늘</button>
        <button class="arrow" @click="goPrev">‹</button>
        <button class="arrow" @click="goNext">›</button>
      </div>
    </div>

    <!-- 뷰 전환 -->
    <div class="seg">
      <button :class="{ on: view === 'month' }" @click="setView('month')">월간</button>
      <button :class="{ on: view === 'list' }" @click="setView('list')">목록</button>
      <button :class="{ on: view === 'tl' }" @click="setView('tl')">타임라인</button>
    </div>

    <!-- 통계 스트립 -->
    <div class="strip">
      <div>오늘 휴가 <b>{{ todayCount }}</b>명</div>
      <div>이번 달 <b>{{ monthCount }}</b>건</div>
      <div class="filter-info">{{ filter === '전체' ? '전체 부서' : filter }}</div>
    </div>

    <!-- 부서 필터칩 -->
    <div class="filters">
      <div
        v-for="d in filterChips"
        :key="d"
        class="chip"
        :class="{ on: d === filter }"
        @click="setFilter(d)"
      >
        <span v-if="d !== '전체'" class="dot" :style="{ background: getColor(d) }"></span>{{ d }}
      </div>
    </div>

    <!-- 월간 + 목록 (FullCalendar) -->
    <div v-show="view !== 'tl'" class="vac-cal">
      <FullCalendar ref="fcRef" :options="calendarOptions" />
    </div>

    <!-- 타임라인 (커스텀) -->
    <div v-show="view === 'tl'" class="tlview">
      <div class="tlhint">← 좌우로 밀어 날짜 확인 · 막대 = 휴가 기간 · 이름줄 고정</div>
      <div class="tlwrap">
        <div class="tl">
          <template v-if="tlPersons.length">
            <!-- 헤더 -->
            <div class="tlrow tlhead" :style="tlGridStyle">
              <div class="tlname">부서 / 이름</div>
              <div
                v-for="d in daysInMonth"
                :key="'h' + d"
                class="tlday"
                :class="{ wk: isWeekendDay(d), td: isTodayDay(d) }"
              >{{ d }}</div>
            </div>
            <!-- 인원별 행 -->
            <div v-for="p in tlPersons" :key="p.id" class="tlrow" :style="tlGridStyle">
              <div class="tlname">
                <i :style="{ background: getColor(p.dept) }"></i>
                <div class="nm"><b>{{ p.name }}</b><small>{{ p.dept }}</small></div>
              </div>
              <div
                v-for="d in daysInMonth"
                :key="p.id + '-' + d"
                class="tlcell"
                :class="tlCellClass(p, d)"
                :style="p.days.has(d) ? { background: getColor(p.dept) } : {}"
              ></div>
            </div>
          </template>
          <div v-else class="empty-msg">해당 조건의 휴가가 없습니다.</div>
        </div>
      </div>
    </div>

    <!-- 범례 -->
    <div class="legend">
      <span v-for="d in departments" :key="'lg' + d">
        <i :style="{ background: getColor(d) }"></i>{{ d }}
      </span>
    </div>

    <!-- 하단 드로어 -->
    <div class="scrim" :class="{ on: sheetOpen }" @click="closeSheet"></div>
    <div class="sheet" :class="{ on: sheetOpen }">
      <div class="grab"></div>
      <div class="sh">
        <div class="t">{{ sheetTitle }}</div>
        <div class="c">{{ sheetEntries.length }}명 휴가</div>
      </div>
      <div class="body">
        <div v-if="!sheetEntries.length" class="empty-msg">휴가자가 없습니다.</div>
        <template v-else>
          <div v-for="(grp, dept) in sheetGrouped" :key="dept" class="deptrow">
            <div class="bar2" :style="{ background: getColor(dept) }"></div>
            <div class="dn">{{ dept }}</div>
            <div class="names">{{ grp.join(', ') }}</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import axiosInstance from "@/api/axios";
import dayjs from "dayjs";

import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

/* ---------- 상태 ---------- */
const fcRef = ref(null);
const view = ref("month");          // 'month' | 'list' | 'tl'
const filter = ref("전체");
const holidays = ref([]);            // [{date, name}]
const rawLeaves = ref([]);           // /leave-data 원본
const vac = ref({});                 // { 'YYYY-MM-DD': [{dept, name, session}] }
const currentMonthStart = ref(dayjs().startOf("month"));
const todayStr = dayjs().format("YYYY-MM-DD");

/* 드로어 */
const sheetOpen = ref(false);
const sheetDate = ref(null);

const getApi = () => fcRef.value && fcRef.value.getApi();

/* ---------- 부서 색상 ---------- */
const DEPT_COLORS = {
  "디자인실": "#5B8DEF",
  "전략기획팀": "#54C28A",
  "온라인사업부": "#23B5B5",
  "구매생산부": "#EF6F8E",
  "무역부": "#F2A35E",
  "원가관리팀": "#9B7EDE",
  "경영지원부": "#F7B36B",
  "구매부": "#E0B84B",
  "생산부": "#C77DD6",
  "디자인연구소": "#46C6C6",
  "수출사업부": "#6C8AE4",
  "베트남": "#E8915A",
};
const FALLBACK_PALETTE = [
  "#7C9CF0", "#5BBF8A", "#3BBFBF", "#EF82A0",
  "#F2AE74", "#A78BE8", "#E0B84B", "#C98BD6",
];
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const getColor = (dept) => {
  if (!dept) return "#E5E5E5";
  return DEPT_COLORS[dept] || FALLBACK_PALETTE[hashStr(dept) % FALLBACK_PALETTE.length];
};

/* ---------- 파생 데이터 ---------- */
// 반휴 시간 표기 (2026-07-15 재지시): 오전 9시~2시 / 오후 2시~6시 —
// 시간 제거 버전에서 되돌림. 이벤트는 종일 유지, 라벨로만 시간 안내.
const sessionLabel = (s) =>
  s === "morning" ? "(오전 9시~2시)" : s === "afternoon" ? "(오후 2시~6시)" : "";

const entriesFor = (dateStr) => {
  const e = vac.value[dateStr] || [];
  return filter.value === "전체" ? e : e.filter((x) => x.dept === filter.value);
};

const PREFERRED_ORDER = Object.keys(DEPT_COLORS);
const departments = computed(() => {
  const set = new Set();
  Object.values(vac.value).forEach((arr) => arr.forEach((x) => set.add(x.dept)));
  return [...set].sort((a, b) => {
    const ia = PREFERRED_ORDER.indexOf(a), ib = PREFERRED_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) {
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    }
    return a.localeCompare(b, "ko");
  });
});
const filterChips = computed(() => ["전체", ...departments.value]);

const monthCount = computed(() => {
  let total = 0;
  Object.keys(vac.value).forEach((k) => (total += entriesFor(k).length));
  return total;
});
const todayCount = computed(() => entriesFor(todayStr).length);

const navTitle = computed(() => currentMonthStart.value.format("YYYY년 M월"));

/* ---------- 타임라인 ---------- */
const daysInMonth = computed(() => currentMonthStart.value.daysInMonth());

const tlPersons = computed(() => {
  const monthPrefix = currentMonthStart.value.format("YYYY-MM");
  const pmap = {};
  Object.keys(vac.value).forEach((k) => {
    if (!k.startsWith(monthPrefix)) return;
    const d = +k.slice(8);
    vac.value[k].forEach((x) => {
      if (filter.value !== "전체" && x.dept !== filter.value) return;
      const id = x.dept + "|" + x.name;
      if (!pmap[id]) pmap[id] = { id, dept: x.dept, name: x.name, days: new Set() };
      pmap[id].days.add(d);
    });
  });
  const arr = Object.values(pmap);
  arr.sort(
    (a, b) =>
      PREFERRED_ORDER.indexOf(a.dept) - PREFERRED_ORDER.indexOf(b.dept) ||
      a.name.localeCompare(b.name, "ko")
  );
  return arr;
});
const tlGridStyle = computed(() => ({
  gridTemplateColumns: `98px repeat(${daysInMonth.value}, 24px)`,
}));
const dowOfDay = (d) => currentMonthStart.value.date(d).day();
const isWeekendDay = (d) => {
  const w = dowOfDay(d);
  return w === 0 || w === 6;
};
const isTodayDay = (d) =>
  currentMonthStart.value.date(d).format("YYYY-MM-DD") === todayStr;
const tlCellClass = (p, d) => {
  const cls = [];
  if (isWeekendDay(d)) cls.push("wk");
  if (isTodayDay(d)) cls.push("td");
  if (p.days.has(d)) {
    cls.push("on");
    if (!p.days.has(d - 1)) cls.push("lr");
    if (!p.days.has(d + 1)) cls.push("rr");
  }
  return cls;
};

/* ---------- 드로어 ---------- */
const sheetEntries = computed(() =>
  sheetDate.value ? entriesFor(sheetDate.value) : []
);
const sheetGrouped = computed(() => {
  const by = {};
  sheetEntries.value.forEach((x) => {
    (by[x.dept] = by[x.dept] || []).push(x.name + sessionLabel(x.session));
  });
  return by;
});
const sheetTitle = computed(() => {
  if (!sheetDate.value) return "";
  const dj = dayjs(sheetDate.value);
  return `${dj.format("M월 D일")} (${["일","월","화","수","목","금","토"][dj.day()]})`;
});
const openSheet = (dateStr) => {
  sheetDate.value = dateStr;
  sheetOpen.value = true;
};
const closeSheet = () => {
  sheetOpen.value = false;
};

/* ---------- 데이터 로드 ---------- */
const fetchHolidaysByYear = async (year) => {
  try {
    const res = await axiosInstance.get(`/holidays/${year}/`);
    if (res?.data?.holidays && Array.isArray(res.data.holidays)) {
      return res.data.holidays.map((h) => ({ date: h.date, name: h.name }));
    }
    return [];
  } catch (e) {
    console.error(`휴일 조회 실패 (${year})`, e);
    return [];
  }
};

const isHolidayDate = (dateStr) =>
  holidays.value.some((h) => h.date === dateStr);

/* /leave-data 응답 → vac(날짜별 명단) */
const buildFromLeaves = () => {
  const next = {};
  rawLeaves.value.forEach((item) => {
    let cur = dayjs(item.start_date);
    const end = dayjs(item.end_date);
    while (cur.isBefore(end) || cur.isSame(end, "day")) {
      const ds = cur.format("YYYY-MM-DD");
      const weekend = cur.day() === 0 || cur.day() === 6;
      if (!weekend && !isHolidayDate(ds)) {
        (next[ds] = next[ds] || []).push({
          dept: item.department_name,
          name: item.employee,
          session: item.session,
        });
      }
      cur = cur.add(1, "day");
    }
  });
  vac.value = next;
};

/* 목록(listMonth)용 이벤트: 하루당 1건씩 — 반휴 포함 전부 종일 이벤트 (2026-07-15) */
const calendarEvents = computed(() => {
  const evs = [];
  Object.keys(vac.value).forEach((ds) => {
    entriesFor(ds).forEach((x, i) => {
      const base = {
        id: `${ds}-${i}`,
        title: `${x.dept} ${x.name}${sessionLabel(x.session)}`,
        backgroundColor: getColor(x.dept),
        borderColor: getColor(x.dept),
        textColor: "#1f2733",
      };
      // 반휴는 종일 이벤트 유지 + 라벨에 시간(오전 9시~2시/오후 2시~6시) 표기
      // (2026-07-15 사이먼 재지시 — 시간 제거 버전에서 되돌림).
      evs.push({ ...base, start: ds, allDay: true });
    });
  });
  return evs;
});

const fetchLeaveData = async (startDate, endDate, startYear, endYear) => {
  try {
    const sy = await fetchHolidaysByYear(startYear);
    const ey = startYear !== endYear ? await fetchHolidaysByYear(endYear) : [];
    holidays.value = [...sy, ...ey];

    const res = await axiosInstance.get(
      `/leave-data/?start=${startDate}&end=${endDate}`
    );
    rawLeaves.value = res.data || [];
    buildFromLeaves();
    refreshViews();   // 월간 셀 페인트 + 목록(FullCalendar) 이벤트 로드
  } catch (e) {
    console.error("휴가 데이터 조회 실패", e);
  }
};

/* ---------- 월간 셀 커스텀 페인트 ---------- */
const cellEls = new Map(); // 'YYYY-MM-DD' -> el

const heatBg = (n) =>
  n <= 0 ? "transparent" : `rgba(59,156,246,${Math.min(0.04 + n * 0.045, 0.5)})`;

const paintCell = (ds, el) => {
  const frame = el.querySelector(".fc-daygrid-day-frame");
  if (!frame) return;
  frame.querySelectorAll(".vac-cell").forEach((n) => n.remove());

  const entries = entriesFor(ds);
  el.style.background = heatBg(entries.length);
  if (!entries.length) return;

  const depts = [...new Set(entries.map((x) => x.dept))];
  const box = document.createElement("div");
  box.className = "vac-cell";

  const dots = document.createElement("div");
  dots.className = "vac-dots";
  depts.slice(0, 6).forEach((dp) => {
    const i = document.createElement("i");
    i.style.background = getColor(dp);
    dots.appendChild(i);
  });
  box.appendChild(dots);

  const cnt = document.createElement("div");
  cnt.className = "vac-cnt";
  cnt.style.background = entries.length >= 8 ? "#ef6f8e" : "#9aa6b6";
  cnt.textContent = `${entries.length}명`;
  box.appendChild(cnt);

  frame.appendChild(box);
};

const paintMonthCells = () => {
  cellEls.forEach((el, ds) => paintCell(ds, el));
};

/* ---------- FullCalendar 옵션 ---------- */
const calendarOptions = ref({
  plugins: [dayGridPlugin, listPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  headerToolbar: false,
  height: "auto",
  weekends: true,
  fixedWeekCount: false,
  showNonCurrentDates: true,
  dayMaxEvents: true,
  timeZone: "Asia/Seoul",
  locale: "ko",
  events: [],
  noEventsContent: "해당 조건의 휴가가 없습니다.",
  dateClick: (arg) => openSheet(dayjs(arg.date).format("YYYY-MM-DD")),
  dayCellClassNames: (arg) => {
    const ds = dayjs(arg.date).format("YYYY-MM-DD");
    const weekend = arg.date.getDay() === 0 || arg.date.getDay() === 6;
    if (weekend) return "fc-weekend-cell";
    if (isHolidayDate(ds)) return "fc-holiday-cell";
    return "";
  },
  dayCellDidMount: (arg) => {
    const ds = dayjs(arg.date).format("YYYY-MM-DD");
    cellEls.set(ds, arg.el);
    paintCell(ds, arg.el);
  },
  dayCellWillUnmount: (arg) => {
    cellEls.delete(dayjs(arg.date).format("YYYY-MM-DD"));
  },
  datesSet: (info) => {
    currentMonthStart.value = dayjs(info.view.currentStart);
    const startDate = dayjs(info.start).format("YYYY-MM-DD");
    const endDate = dayjs(info.end).format("YYYY-MM-DD");
    fetchLeaveData(
      startDate,
      endDate,
      dayjs(info.start).year(),
      dayjs(info.end).year()
    );
  },
});

/* 이벤트(목록) + 월간 셀 갱신 — 옵션 반응형 갱신이라 초기 렌더 타이밍에 안전 */
const refreshViews = () => {
  calendarOptions.value.events = calendarEvents.value;
  paintMonthCells();
};

/* ---------- 액션 ---------- */
const setView = (v) => {
  view.value = v;
  const api = getApi();
  if (!api) return;
  if (v === "month") api.changeView("dayGridMonth");
  else if (v === "list") api.changeView("listMonth");
};
const setFilter = (f) => {
  filter.value = f;
  refreshViews();
};
const goPrev = () => getApi() && getApi().prev();
const goNext = () => getApi() && getApi().next();
const goToday = () => getApi() && getApi().today();
</script>

<style scoped>
.vac-app {
  --blue: #3b9cf6;
  --ink: #1f2733;
  --muted: #8a94a6;
  --line: #e8ecf2;
  --bg: #f4f6f9;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: var(--bg);
  color: var(--ink);
  font-family: -apple-system, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif;
  border-radius: 14px;
  overflow: hidden;
  padding-bottom: 8px;
}

.vac-nav { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 4px; }
.vac-nav .m { font-size: 20px; font-weight: 800; }
.vac-nav .ctrl { display: flex; gap: 6px; align-items: center; }
.vac-nav button {
  border: 1px solid var(--line); background: #fff; border-radius: 9px; height: 34px;
  padding: 0 12px; font-size: 14px; color: var(--ink); font-weight: 600; cursor: pointer;
}
.vac-nav .arrow { width: 34px; padding: 0; font-size: 16px; }

.seg { display: flex; margin: 10px 16px 8px; background: #e9eef5; border-radius: 11px; padding: 4px; }
.seg button {
  flex: 1; border: none; background: transparent; height: 36px; border-radius: 8px;
  font-size: 13.5px; font-weight: 700; color: var(--muted); cursor: pointer;
}
.seg button.on { background: #fff; color: var(--ink); box-shadow: 0 1px 3px rgba(0,0,0,.08); }

.strip {
  margin: 0 16px 10px; background: #fff; border: 1px solid var(--line); border-radius: 12px;
  padding: 11px 14px; font-size: 13px; color: var(--muted); display: flex; gap: 14px; align-items: center;
}
.strip b { color: var(--ink); font-size: 15px; }
.strip .filter-info { margin-left: auto; }

.filters { display: flex; gap: 7px; overflow-x: auto; padding: 0 16px 12px; scrollbar-width: none; flex-wrap: wrap; }
.filters::-webkit-scrollbar { display: none; }
.chip {
  flex: 0 0 auto; border: 1px solid var(--line); background: #fff; border-radius: 20px;
  height: 32px; padding: 0 13px; font-size: 13px; font-weight: 600; color: #5b6675;
  display: flex; align-items: center; gap: 6px; cursor: pointer;
}
.chip .dot { width: 9px; height: 9px; border-radius: 50%; }
.chip.on { background: var(--ink); color: #fff; border-color: var(--ink); }

.vac-cal { margin: 0 12px 16px; }

/* 타임라인 */
.tlview { margin: 0 12px 16px; }
.tlhint { font-size: 12px; color: var(--muted); margin: 0 4px 8px; }
.tlwrap { overflow: auto; border: 1px solid var(--line); border-radius: 12px; background: #fff; max-height: 62vh; }
.tl { min-width: max-content; }
.tlrow { display: grid; }
.tlhead { position: sticky; top: 0; z-index: 5; background: #f7f9fc; border-bottom: 1px solid var(--line); }
.tlname {
  position: sticky; left: 0; z-index: 4; background: #fff; display: flex; align-items: center;
  gap: 7px; padding: 6px 8px; border-right: 1px solid var(--line); border-bottom: 1px solid #f1f4f8;
}
.tlhead .tlname { background: #f7f9fc; font-weight: 800; color: #5b6675; z-index: 6; }
.tlname i { width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }
.tlname .nm { display: flex; flex-direction: column; line-height: 1.2; }
.tlname .nm b { font-size: 12.5px; font-weight: 700; }
.tlname .nm small { color: var(--muted); font-weight: 600; font-size: 10px; }
.tlday { font-size: 11px; text-align: center; padding: 8px 0; color: #7b8698; font-weight: 700; }
.tlday.wk { color: #c97a92; }
.tlday.td { background: rgba(59,156,246,.15); color: var(--blue); }
.tlcell { height: 34px; border-right: 1px solid #f4f6fa; border-bottom: 1px solid #f1f4f8; }
.tlcell.wk { background: #fafbfd; }
.tlcell.td { background: rgba(59,156,246,.06); }
.tlcell.on { border-right-color: transparent; }
.tlcell.lr { border-top-left-radius: 9px; border-bottom-left-radius: 9px; }
.tlcell.rr { border-top-right-radius: 9px; border-bottom-right-radius: 9px; border-right: 1px solid transparent; }

.empty-msg { padding: 34px 0; text-align: center; color: var(--muted); font-size: 14px; }

.legend { display: flex; flex-wrap: wrap; gap: 10px 16px; padding: 0 18px 18px; font-size: 12px; color: #5b6675; }
.legend span { display: flex; align-items: center; gap: 6px; }
.legend i { width: 10px; height: 10px; border-radius: 50%; }

/* 드로어 */
.scrim { position: fixed; inset: 0; background: rgba(20,28,40,.45); opacity: 0; pointer-events: none; transition: .2s; z-index: 1900; }
.scrim.on { opacity: 1; pointer-events: auto; }
.sheet {
  position: fixed; left: 50%; transform: translateX(-50%) translateY(100%); bottom: 0;
  width: 100%; max-width: 480px; background: #fff; border-radius: 18px 18px 0 0; z-index: 2000;
  transition: transform .26s cubic-bezier(.2,.8,.2,1); max-height: 78vh; display: flex; flex-direction: column;
}
.sheet.on { transform: translateX(-50%) translateY(0); }
.sheet .grab { width: 42px; height: 5px; background: #d7dde6; border-radius: 3px; margin: 9px auto 4px; }
.sheet .sh { padding: 6px 18px 12px; border-bottom: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; }
.sheet .sh .t { font-size: 17px; font-weight: 800; }
.sheet .sh .c { font-size: 13px; color: var(--muted); font-weight: 700; }
.sheet .body { overflow: auto; padding: 6px 0 24px; }

.deptrow { display: flex; gap: 9px; padding: 10px 14px; border-bottom: 1px dashed #eef1f6; }
.deptrow:last-child { border-bottom: none; }
.deptrow .bar2 { width: 4px; border-radius: 4px; flex: 0 0 auto; }
.deptrow .dn { flex: 0 0 96px; font-size: 12px; font-weight: 700; color: #5b6675; padding-top: 2px; }
.deptrow .names { font-size: 13.5px; line-height: 1.7; color: var(--ink); }
</style>

<!-- FullCalendar 내부 요소는 scoped로 닿지 않으므로 :deep 사용 -->
<style scoped>
/* 월간 셀: 기본 이벤트 숨기고 커스텀 점/배지 노출 */
.vac-cal :deep(.fc-daygrid-day-events) { display: none; }
.vac-cal :deep(.fc-daygrid-day-frame) { position: relative; min-height: 74px; }
.vac-cal :deep(.fc-daygrid-day-top) { flex-direction: row; }
.vac-cal :deep(.fc-col-header-cell) { padding: 6px 0; font-weight: 700; color: var(--muted); }
.vac-cal :deep(.fc-daygrid-day-number) { font-size: 12px; font-weight: 700; color: #586173; }
.vac-cal :deep(.fc-day-sun .fc-daygrid-day-number),
.vac-cal :deep(.fc-day-sun.fc-col-header-cell) { color: #ef6f6f; }
.vac-cal :deep(.fc-day-sat .fc-daygrid-day-number),
.vac-cal :deep(.fc-day-sat.fc-col-header-cell) { color: #5b8def; }
.vac-cal :deep(.fc-day-today) { background: transparent !important; }
.vac-cal :deep(.fc-day-today .fc-daygrid-day-frame) {
  box-shadow: inset 0 0 0 2px rgba(59,156,246,.4); border-radius: 8px;
}
.vac-cal :deep(.fc-weekend-cell) { background: #fafbfd; }
.vac-cal :deep(.fc-holiday-cell) { background: #fff5ec; }

.vac-cal :deep(.vac-cell) { position: absolute; left: 5px; right: 5px; bottom: 5px; pointer-events: none; }
.vac-cal :deep(.vac-dots) { display: flex; flex-wrap: wrap; gap: 3px; }
.vac-cal :deep(.vac-dots i) { width: 8px; height: 8px; border-radius: 50%; display: block; }
.vac-cal :deep(.vac-cnt) {
  position: absolute; right: 0; bottom: 0; font-size: 11px; font-weight: 800;
  color: #fff; border-radius: 9px; padding: 1px 6px; line-height: 1.5;
}

/* 목록(listMonth): 시안 느낌으로 재단장 */
.vac-cal :deep(.fc-list) { border: none; }
.vac-cal :deep(.fc-list-day-cushion) { background: #f7f9fc; font-weight: 800; }
.vac-cal :deep(.fc-list-event:hover td) { background: #f4f8ff; }
.vac-cal :deep(.fc-list-event-dot) { border-width: 5px; }
.vac-cal :deep(.fc-list-event-title) { font-size: 13.5px; font-weight: 600; }
</style>
